<template>
    <div>
        <Alerts :_success="success" :_error="error" :_loading="wait"></Alerts>

        <!-- Enhanced Tab Navigation -->
        <ul class="nav nav-tabs nav-justified border-0 bg-light rounded-top">
            <li class="nav-item" v-for="(tab, index) in tabs" :key="index">
                <a class="nav-link d-flex align-items-center justify-content-center"
                   :class="{ active: activeTab === index }"
                   href="#"
                   @click.prevent="setActiveTab(index)">
                    <i :class="tab.icon" class="mr-2"></i>
                    <span class="d-none d-md-inline">{{ tab.name }}</span>
                    <span class="d-md-none">{{ tab.shortName }}</span>
                    <span v-if="tab.badge && leaves.length > 0"
                          class="badge badge-primary badge-pill ml-2">
                        {{ leaves.length }}
                    </span>
                </a>
            </li>
        </ul>

        <!-- Tab Content Container -->
        <div class="tab-content border border-top-0 bg-white rounded-bottom shadow-sm">
            <!-- TAB 1: Leave Types & Policy -->
            <div v-if="activeTab === 0" class="p-4">
                <div class="mb-4">
                    <h4 class="font-weight-bold mb-2">📚 Leave Types & Policy Guidelines</h4>
                    <p class="text-muted">
                        Understanding your leave entitlements and how to apply for them
                    </p>
                </div>

                <!-- Policy Information Card -->
                <div class="card mb-4 border-info">
                    <div class="card-body">
                        <h5 class="font-weight-bold mb-3">📋 Leave Policy Overview</h5>
                        <p class="mb-3">
                            This leave policy applies to all full-time permanent staff and consultants
                            on a minimum one-year contract with the organization.
                        </p>
                        <a :href="policy_url"
                           target="_blank"
                           class="btn btn-outline-primary">
                            <i class="fas fa-download"></i> Download Complete Policy Guideline
                        </a>
                    </div>
                </div>

                <!-- Leave Types Accordion -->
                <div class="card">
                    <div class="card-header bg-primary text-white">
                        <h5 class="mb-0">
                            <i class="fas fa-list"></i> Available Leave Types
                        </h5>
                    </div>
                    <div class="card-body p-0">
                        <div id="leaveAccordion" class="accordion">
                            <div v-for="(leave, index) in leave_types"
                                 :key="index"
                                 class="border-bottom">
                                <div class="p-3" :id="'heading' + index">
                                    <a href="javascript:void(0)"
                                       class="d-flex justify-content-between align-items-center text-decoration-none"
                                       :class="{ 'font-weight-bold': index === activeIndex }"
                                       @click="toggleAccordion(index)">
                                        <span>
                                            <i class="fas"
                                               :class="index === activeIndex ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                                            <span class="ml-2">{{ leave.name }}</span>
                                            <span v-if="leave.id == 1"
                                                  class="badge badge-danger ml-2">NEW</span>
                                        </span>
                                        <span v-if="leave.balance !== undefined"
                                              class="badge badge-pill"
                                              :class="leave.balance > 0 ? 'badge-success' : 'badge-secondary'">
                                            {{ leave.balance }} days available
                                        </span>
                                    </a>
                                </div>
                                <div :id="'collapse' + index"
                                     class="collapse"
                                     :class="{ show: index === activeIndex }"
                                     :aria-labelledby="'heading' + index">
                                    <div class="p-3 bg-light border-top" v-html="leave.description"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Quick Action -->
                <div class="text-center mt-4">
                    <button class="btn btn-success btn-lg" @click="activeTab = 2">
                        <i class="fas fa-plus-circle"></i> Apply for Leave Now
                    </button>
                </div>
            </div>

            <!-- TAB 2: My Applications -->
            <div v-if="activeTab === 1" class="p-4">
                <!-- Empty State -->
                <div v-if="leaves.length === 0" class="text-center py-5">
                    <div class="mb-4">
                        <i class="fas fa-calendar-times fa-4x text-muted"></i>
                    </div>
                    <h5 class="text-muted mb-3">No Leave Applications Yet</h5>
                    <p class="text-muted mb-4">
                        You haven't applied for any leave days. Start by submitting your first application.
                    </p>
                    <button class="btn btn-success btn-lg" @click="activeTab = 2">
                        <i class="fas fa-plus-circle"></i> Apply for Leave
                    </button>
                </div>

                <!-- Leave Applications List -->
                <div v-else>
                    <!-- Single Record View -->
                    <div v-if="record">
                        <div class="d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h4 class="font-weight-bold mb-1">
                                    {{ record.leave_type.name }}
                                </h4>
                                <p class="text-muted mb-0">
                                    <i class="fas fa-calendar"></i>
                                    {{ day(record.start_date).format('MMM DD, YYYY') }}
                                    →
                                    {{ day(record.end_date).format('MMM DD, YYYY') }}
                                    <span class="badge badge-pill ml-2"
                                          :class="{
                                              'badge-primary': record.days === 1,
                                              'badge-info': record.days > 1
                                          }">
                                        {{ record.days }} {{ record.days === 1 ? 'day' : 'days' }}
                                    </span>
                                </p>
                            </div>
                            <button @click="record = ''" class="btn btn-outline-secondary">
                                <i class="fas fa-arrow-left"></i> Back to List
                            </button>
                        </div>

                        <!-- Leave Calendar -->
                        <LeaveCalendar class="mb-4" :leave="record"></LeaveCalendar>

                        <div class="row">
                            <!-- Leave Details Card -->
                            <div class="col-md-7">
                                <div class="card mb-4">
                                    <div class="card-header bg-light">
                                        <h6 class="mb-0 font-weight-bold">
                                            <i class="fas fa-info-circle"></i> Leave Details
                                        </h6>
                                    </div>
                                    <div class="card-body">
                                        <table class="table table-borderless mb-0">
                                            <tr>
                                                <th style="width: 35%">
                                                    <i class="fas fa-clock text-muted"></i> Date Applied
                                                </th>
                                                <td>{{ day(record.created_at).format('MMM DD, YYYY h:mm A') }}</td>
                                            </tr>
                                            <tr>
                                                <th>
                                                    <i class="fas fa-flag text-muted"></i> Status
                                                </th>
                                                <td>
                                                    <span class="badge badge-pill px-3 py-2"
                                                          :class="{
                                                              'badge-success': record.status === 'approved',
                                                              'badge-warning': record.status === 'pending',
                                                              'badge-danger': record.status === 'declined',
                                                              'badge-info': record.status === 'verified'
                                                          }">
                                                        {{ record.status.toUpperCase() }}
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>
                                                    <i class="fas fa-tag text-muted"></i> Leave Type
                                                </th>
                                                <td>{{ record.leave_type.name }}</td>
                                            </tr>
                                            <tr>
                                                <th>
                                                    <i class="fas fa-calendar-day text-muted"></i> Duration
                                                </th>
                                                <td><strong>{{ record.days }}</strong> {{ record.days === 1 ? 'Day' : 'Days' }}</td>
                                            </tr>
                                            <tr>
                                                <th>
                                                    <i class="fas fa-calendar-check text-muted"></i> Start Date
                                                </th>
                                                <td>{{ day(record.start_date).format('MMM DD, YYYY') }}</td>
                                            </tr>
                                            <tr>
                                                <th>
                                                    <i class="fas fa-calendar-times text-muted"></i> End Date
                                                </th>
                                                <td>{{ day(record.end_date).format('MMM DD, YYYY') }}</td>
                                            </tr>
                                            <tr>
                                                <th>
                                                    <i class="fas fa-comment text-muted"></i> Comments
                                                </th>
                                                <td>{{ record.memo || 'No additional comments' }}</td>
                                            </tr>
                                        </table>
                                    </div>
                                </div>

                                <!-- Action Buttons -->
                                <div class="d-flex">
                                    <button @click="record = ''" class="btn btn-outline-secondary mr-2">
                                        <i class="fas fa-arrow-left"></i> Back
                                    </button>
                                    <template v-if="canEdit()">
                                        <button @click.prevent="editLeave(record)" class="btn btn-primary mr-2">
                                            <i class="fas fa-edit"></i> Edit
                                        </button>
                                        <button @click.prevent="deleteLeave(record)" class="btn btn-danger">
                                            <i class="fas fa-trash"></i> Delete
                                        </button>
                                    </template>
                                </div>
                            </div>

                            <!-- Approval Status Card -->
                            <div class="col-md-5">
                                <!-- Authorization Status -->
                                <div class="card mb-3"
                                     :class="{
                                         'border-warning': !record.isAuthorized && record.status === 'pending',
                                         'border-success': record.isAuthorized || record.isApproved
                                     }">
                                    <div class="card-header"
                                         :class="{
                                             'bg-warning text-white': !record.isAuthorized && record.status === 'pending',
                                             'bg-success text-white': record.isAuthorized || record.isApproved
                                         }">
                                        <h6 class="mb-0 font-weight-bold">
                                            <i class="fas fa-user-check"></i> Authorization Status
                                        </h6>
                                    </div>
                                    <div class="card-body">
                                        <div v-if="record.isAuthorized">
                                            <div class="alert alert-success mb-2">
                                                <i class="fas fa-check-circle"></i>
                                                <strong>Authorized</strong>
                                            </div>
                                            <p class="small mb-2">
                                                <strong>Authorized on:</strong><br>
                                                {{ day(record.approval.authorized_at).format('MMM DD, YYYY h:mm A') }}
                                            </p>
                                            <p v-if="record.status === 'approved'" class="text-info small mb-0">
                                                <i class="fas fa-info-circle"></i>
                                                Waiting for final verification
                                            </p>
                                        </div>
                                        <div v-else>
                                            <div class="alert alert-warning mb-2">
                                                <i class="fas fa-clock"></i>
                                                <strong>{{ record.status.charAt(0).toUpperCase() + record.status.slice(1) }}</strong>
                                            </div>
                                            <p class="small text-muted mb-0">
                                                Your request is being reviewed. You'll receive a notification on Flock and
                                                in your <router-link to="/notifications">notifications</router-link> once processed.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Approval Details -->
                                <div v-if="record.isApproved" class="card mb-3 border-success">
                                    <div class="card-header bg-success text-white">
                                        <h6 class="mb-0 font-weight-bold">
                                            <i class="fas fa-check-circle"></i> Approval Details
                                        </h6>
                                    </div>
                                    <div class="card-body">
                                        <div class="alert alert-success mb-3">
                                            <i class="fas fa-thumbs-up"></i>
                                            <strong>Your leave has been approved!</strong>
                                        </div>
                                        <div class="small">
                                            <p class="mb-2">
                                                <strong><i class="fas fa-calendar-check"></i> Approved On:</strong><br>
                                                <span class="text-success">
                                                    {{ day(record.approval.approved_at).format('MMM DD, YYYY h:mm A') }}
                                                </span>
                                            </p>
                                            <p class="mb-2">
                                                <strong><i class="fas fa-user"></i> Approved By:</strong><br>
                                                {{ record.approval.approved_by.name }}
                                            </p>
                                            <p class="mb-0">
                                                <strong><i class="fas fa-comment-dots"></i> Remarks:</strong><br>
                                                {{ record.approval.approval_remarks || 'No remarks provided' }}
                                            </p>
                                        </div>
                                        <div v-if="record.status === 'approved'"
                                             class="alert alert-info mt-3 mb-0 small">
                                            <i class="fas fa-hourglass-half"></i>
                                            Pending final verification
                                        </div>
                                    </div>
                                </div>

                                <!-- Decline Details -->
                                <div v-if="record.isDeclined" class="card border-danger">
                                    <div class="card-header bg-danger text-white">
                                        <h6 class="mb-0 font-weight-bold">
                                            <i class="fas fa-times-circle"></i> Decline Details
                                        </h6>
                                    </div>
                                    <div class="card-body">
                                        <div class="alert alert-danger mb-3">
                                            <i class="fas fa-exclamation-triangle"></i>
                                            <strong>Your leave request was declined</strong>
                                        </div>
                                        <div class="small">
                                            <p class="mb-2">
                                                <strong><i class="fas fa-calendar-times"></i> Declined On:</strong><br>
                                                <span class="text-danger">
                                                    {{ day(record.approval.declined_at).format('MMM DD, YYYY h:mm A') }}
                                                </span>
                                            </p>
                                            <p class="mb-2">
                                                <strong><i class="fas fa-user"></i> Declined By:</strong><br>
                                                {{ record.approval.declined_by.name }}
                                            </p>
                                            <p class="mb-0">
                                                <strong><i class="fas fa-exclamation-circle"></i> Reason:</strong><br>
                                                <span class="text-danger">
                                                    {{ record.approval.decline_remarks || 'No reason provided' }}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Applications List View -->
                    <div v-else>
                        <div class="d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h4 class="font-weight-bold mb-1">My Leave Applications</h4>
                                <p class="text-muted mb-0">
                                    View and manage all your leave requests
                                </p>
                            </div>
                            <button class="btn btn-success" @click="activeTab = 2">
                                <i class="fas fa-plus"></i> New Application
                            </button>
                        </div>

                        <div class="row">
                            <div v-for="i in leaves" :key="i.id" class="col-md-6 col-lg-4 mb-3">
                                <div class="card h-100 shadow-sm hover-shadow"
                                     @click="record = i"
                                     style="cursor: pointer; transition: all 0.3s ease;">
                                    <div class="card-body">
                                        <div class="d-flex justify-content-between align-items-start mb-2">
                                            <h6 class="font-weight-bold mb-0">{{ i.leave_type.name }}</h6>
                                            <span class="badge badge-pill"
                                                  :class="{
                                                      'badge-success': i.status === 'approved',
                                                      'badge-warning': i.status === 'pending',
                                                      'badge-danger': i.status === 'declined',
                                                      'badge-info': i.status === 'verified'
                                                  }">
                                                {{ i.status }}
                                            </span>
                                        </div>
                                        <p class="text-muted small mb-2">
                                            <i class="fas fa-calendar"></i>
                                            {{ day(i.start_date).format('MMM DD, YYYY') }}
                                        </p>
                                        <p class="text-muted small mb-3">
                                            <i class="fas fa-arrow-right"></i>
                                            {{ day(i.end_date).format('MMM DD, YYYY') }}
                                        </p>
                                        <div class="d-flex justify-content-between align-items-center">
                                            <span class="badge badge-primary">
                                                {{ i.days }} {{ i.days === 1 ? 'day' : 'days' }}
                                            </span>
                                            <button class="btn btn-sm btn-outline-primary" @click.stop="record = i">
                                                View Details <i class="fas fa-chevron-right"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- TAB 3: Apply for Leave -->
            <div v-if="activeTab === 2" class="p-4">
                <div class="row justify-content-center">
                    <div class="col-lg-8">
                        <div class="text-center mb-4">
                            <h4 class="font-weight-bold mb-2">
                                <i class="fas fa-file-alt"></i> Leave Application Form
                            </h4>
                            <p class="text-muted">
                                Complete all required fields to submit your leave request
                            </p>
                        </div>

                        <form @submit.prevent="sendApplication()">
                            <div class="card mb-4">
                                <div class="card-header bg-light">
                                    <h6 class="mb-0 font-weight-bold">
                                        <i class="fas fa-info-circle"></i> Leave Information
                                    </h6>
                                </div>
                                <div class="card-body">
                                    <!-- Leave Type -->
                                    <div class="form-group">
                                        <label class="font-weight-bold">
                                            Type of Leave <span class="text-danger">*</span>
                                        </label>
                                        <multiselect
                                            :options="leave_types"
                                            v-model="selected_leave"
                                            track-by="id"
                                            label="name"
                                            placeholder="Select a leave type"
                                            :searchable="true"
                                            :allow-empty="false"
                                            :hide-selected="true"
                                            :disabled="wait"
                                            @input="setSelectedLeaveType"
                                        ></multiselect>
                                        <div v-if="selected_leave && selected_leave.description"
                                             class="alert alert-info mt-2 small mb-0">
                                            <div v-html="selected_leave.description"></div>
                                            <span v-if="selected_leave.id == 1"
                                                  class="badge badge-danger ml-2">NEW</span>
                                        </div>
                                    </div>

                                    <!-- Document Upload for Maternity/Paternity -->
                                    <div v-if="leave.type === 'Paternity' || leave.type === 'Maternity'"
                                         class="form-group">
                                        <label class="font-weight-bold">
                                            Supporting Documents <span class="text-danger">*</span>
                                        </label>
                                        <input type="file" class="form-control-file"/>
                                        <small class="form-text text-muted">
                                            <i class="fas fa-info-circle"></i>
                                            Please attach: birth notification, medical records, or adoption documents
                                        </small>
                                    </div>

                                    <!-- Start Date -->
                                    <div class="form-group">
                                        <label class="font-weight-bold">
                                            Start Date <span class="text-danger">*</span>
                                        </label>
                                        <Datepicker
                                            :bootstrap-styling="true"
                                            v-model="leave.start_date"
                                            maximum-view="day"
                                            :disabled="wait"
                                            placeholder="Select when your leave begins"
                                            :required="true"
                                            :disabled-dates="disabledDates"
                                            @selected="getLeaveEndDate"
                                        ></Datepicker>
                                        <small class="form-text text-muted">
                                            <i class="fas fa-info-circle"></i>
                                            Weekend days are excluded automatically
                                        </small>
                                    </div>

                                    <!-- Number of Days -->
                                    <div class="form-group">
                                        <label class="font-weight-bold">
                                            Number of Leave Days <span class="text-danger">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            class="form-control form-control-lg"
                                            v-model="leave.days"
                                            @change="getLeaveEndDate"
                                            placeholder="Enter number of days (e.g., 5)"
                                            :disabled="wait || !leave.start_date"/>

                                        <!-- Balance Display -->
                                        <div v-if="selected_leave && selected_leave.balance !== undefined"
                                             class="alert mt-2 mb-0"
                                             :class="selected_leave.balance === 0 ? 'alert-danger' : 'alert-success'">
                                            <div class="d-flex justify-content-between align-items-center">
                                                <span>
                                                    <i class="fas fa-calendar-check"></i>
                                                    <strong>Available Balance:</strong>
                                                </span>
                                                <span class="badge badge-pill badge-lg"
                                                      :class="selected_leave.balance === 0 ? 'badge-danger' : 'badge-success'">
                                                    {{ selected_leave.balance }} days
                                                </span>
                                            </div>
                                            <small v-if="leave.id" class="d-block mt-1 text-muted">
                                                Note: This excludes the {{ leave.days }} days from your current edit
                                            </small>
                                        </div>
                                    </div>

                                    <!-- End Date (Calculated) -->
                                    <div v-if="leave_end_date" class="form-group">
                                        <label class="font-weight-bold">
                                            End Date (Calculated)
                                        </label>
                                        <div class="alert alert-success mb-0">
                                            <div class="d-flex justify-content-between align-items-center">
                                                <span>
                                                    <i class="fas fa-calendar-check"></i>
                                                    Your leave will end on:
                                                </span>
                                                <strong class="h5 mb-0">{{ leave_end_date }}</strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="card mb-4">
                                <div class="card-header bg-light">
                                    <h6 class="mb-0 font-weight-bold">
                                        <i class="fas fa-user-friends"></i> Handover Details
                                    </h6>
                                </div>
                                <div class="card-body">
                                    <!-- Handover Staff -->
                                    <div class="form-group mb-0">
                                        <label class="font-weight-bold">
                                            Handover Staff <span class="text-danger">*</span>
                                        </label>
                                        <multiselect
                                            v-model="handover_staff"
                                            deselect-label="Already selected"
                                            track-by="id"
                                            label="name"
                                            placeholder="Search and select a staff member"
                                            :options="staff"
                                            :searchable="true"
                                            :allow-empty="false"
                                            :hide-selected="true"
                                            :disabled="wait"
                                            @input="setHandoverStaffId"
                                        ></multiselect>
                                        <small class="form-text text-muted">
                                            <i class="fas fa-info-circle"></i>
                                            Select the colleague who will handle your responsibilities during your absence
                                        </small>
                                    </div>
                                </div>
                            </div>

                            <div class="card mb-4">
                                <div class="card-header bg-light">
                                    <h6 class="mb-0 font-weight-bold">
                                        <i class="fas fa-comment"></i> Additional Information
                                    </h6>
                                </div>
                                <div class="card-body">
                                    <!-- Additional Comments -->
                                    <div class="form-group mb-0">
                                        <label class="font-weight-bold">
                                            Additional Comments
                                            <span class="text-muted font-weight-normal">(Optional)</span>
                                        </label>
                                        <textarea class="form-control"
                                                  rows="4"
                                                  v-model="leave.memo"
                                                  placeholder="Provide any additional information that may help with your leave request..."
                                                  :disabled="wait"></textarea>
                                        <small class="form-text text-muted">
                                            Examples: emergency contacts, project status, special circumstances
                                        </small>
                                    </div>
                                </div>
                            </div>

                            <!-- Submit Button -->
                            <div class="card border-0 shadow-sm">
                                <div class="card-body">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <div>
                                            <button type="submit"
                                                    class="btn btn-success btn-lg"
                                                    :disabled="wait || !leave.type || !leave.start_date || !leave.days || !leave.handover_staff">
                                                <i v-if="wait" class="fas fa-spinner fa-spin"></i>
                                                <i v-else class="fas fa-paper-plane"></i>
                                                <span v-if="wait">Submitting...</span>
                                                <span v-else>Submit Application</span>
                                            </button>
                                            <button type="button"
                                                    class="btn btn-outline-secondary btn-lg ml-2"
                                                    @click="activeTab = 1"
                                                    :disabled="wait">
                                                Cancel
                                            </button>
                                        </div>
                                        <div v-if="!leave.type || !leave.start_date || !leave.days || !leave.handover_staff"
                                             class="text-danger small">
                                            <i class="fas fa-exclamation-triangle"></i>
                                            Please complete all required fields
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <!-- TAB 4: Download Report -->
            <div v-if="activeTab === 3" class="p-4">
                <div class="row justify-content-center">
                    <div class="col-lg-8">
                        <div class="text-center mb-4">
                            <h4 class="font-weight-bold mb-2">
                                <i class="fas fa-file-download"></i> Leave Report
                            </h4>
                            <p class="text-muted">
                                Generate a comprehensive report of your leave history and balance
                            </p>
                        </div>

                        <!-- User Info Card -->
                        <div class="card mb-4">
                            <div class="card-body">
                                <div class="d-flex align-items-center">
                                    <div class="mr-3">
                                        <i class="fas fa-user-circle fa-3x text-primary"></i>
                                    </div>
                                    <div>
                                        <h6 class="font-weight-bold mb-1">{{ auth.name }}</h6>
                                        <p class="text-muted mb-0 small">{{ auth.email }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Generate Report Button -->
                        <div class="card mb-4 border-primary">
                            <div class="card-body text-center">
                                <button class="btn btn-primary btn-lg"
                                        :disabled="wait"
                                        @click="generateReport()">
                                    <i v-if="wait" class="fas fa-spinner fa-spin"></i>
                                    <i v-else class="fas fa-file-pdf"></i>
                                    <span v-if="reportUrl">Regenerate</span>
                                    <span v-else>Generate</span>
                                    Report
                                </button>
                                <p v-if="wait" class="text-muted small mt-2 mb-0">
                                    <i class="fas fa-hourglass-half"></i>
                                    Generating your report, please wait...
                                </p>
                            </div>
                        </div>

                        <!-- Report Preview -->
                        <div class="card">
                            <div class="card-header bg-light">
                                <h6 class="mb-0 font-weight-bold">
                                    <i class="fas fa-eye"></i> Report Preview
                                </h6>
                            </div>
                            <div class="card-body p-0">
                                <iframe v-if="reportUrl"
                                        :src="reportUrl"
                                        width="100%"
                                        style="height: 75vh; border: none;">
                                </iframe>
                                <div v-else class="text-center py-5">
                                    <i class="fas fa-file-pdf fa-4x text-muted mb-3"></i>
                                    <p class="text-muted mb-0">
                                        Click the "Generate Report" button above to view your leave report
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Alerts from "@/components/Alerts";
import Datepicker from 'vuejs-datepicker'
import Multiselect from 'vue-multiselect'
import LeaveCalendar from "@/components/helpers/LeaveCalendar.vue";

export default {
    name: "Leave",
    props: ['auth'],
    components: {LeaveCalendar, Alerts, Datepicker, Multiselect},

    data() {
        return {
            tabs: [
                {
                    name: "Types & Policy",
                    shortName: "Policy",
                    icon: "fas fa-book",
                    badge: false
                },
                {
                    name: "My Applications",
                    shortName: "Applications",
                    icon: "fas fa-list-alt",
                    badge: true
                },
                {
                    name: "Apply Now",
                    shortName: "Apply",
                    icon: "fas fa-plus-circle",
                    badge: false
                },
                {
                    name: "Download Report",
                    shortName: "Report",
                    icon: "fas fa-download",
                    badge: false
                },
            ],
            activeTab: 0,

            leaves: [],
            selected_leave: {},
            leave_end_date: '',
            leave: {
                id: '',
                type: '',
                start_date: '',
                days: 0,
                handover_staff: '',
                memo: ''
            },
            policy_url: 'https://docs.google.com/document/d/1A4i3bhmsiyKo67CZszoK5HHWdKuoErZN/edit',
            record: '',
            staff: [],
            handover_staff: '',
            leave_types: [],
            disabledDates: {
                days: [6, 0], // Disable weekends
                from: new Date(new Date().setDate(new Date().getDate() + 180)),
                to: new Date(new Date().setDate(new Date().getDate() - 30))
            },

            activeIndex: 0,
            reportUrl: '',

            // Missing data properties
            wait: false,
            success: '',
            error: ''
        }
    },
    mounted() {
        this.loadLeaveTypes()
        this.loadLeaves()
        this.loadStaff()
    },
    computed: {
        today() {
            return new Date()
        }
    },
    methods: {
        setActiveTab(index) {
            this.activeTab = index;
            // Clear any previous state when switching tabs
            if (index === 2) {
                this.emptyForm();
            }
        },

        toggleAccordion(index) {
            this.activeIndex = this.activeIndex === index ? -1 : index;
        },

        setHandoverStaffId() {
            if (this.handover_staff && this.handover_staff.id) {
                this.leave.handover_staff = this.handover_staff.id
            }
        },

        setSelectedLeaveType() {
            if (this.selected_leave && this.selected_leave.id) {
                this.leave.type = this.selected_leave.id
            }
        },

        generateReport() {
            this.clear()
            this.setLoading()

            this.$http.post('leave/report', {user_id: this.auth.id}, {responseType: 'blob'})
                .then(response => {
                    const pdfBlob = new Blob([response.data], {type: 'application/pdf'});
                    this.reportUrl = URL.createObjectURL(pdfBlob);
                    this.success = 'Report generated successfully!'
                    setTimeout(() => (this.success = ''), 2000)
                })
                .catch(error => {
                    this.showError(error)
                })
                .finally(() => {
                    this.wait = false
                })
        },

        getLeaveEndDate() {
            if (this.leave.start_date && this.leave.days) {
                this.leave_end_date = 'Calculating...'

                this.$http.post('leave/end_date', this.leave)
                    .then(response => {
                        this.leave_end_date = response.data.data
                    })
                    .catch(error => {
                        this.showError(error)
                        this.leave_end_date = ''
                    })
            }
        },

        loadLeaveTypes() {
            this.setLoading()

            this.$http.get('leave/types')
                .then(response => {
                    this.leave_types = response.data.data
                })
                .catch(error => {
                    this.showError(error)
                })
                .finally(() => {
                    this.wait = false
                })
        },

        loadLeaves() {
            this.$http.get('user/leaves')
                .then(response => {
                    this.leaves = response.data.data
                })
                .catch(error => {
                    this.showError(error)
                })
        },

        loadStaff() {
            this.$http.get('staff')
                .then(response => {
                    this.staff = response.data.data
                })
                .catch(error => {
                    this.showError(error)
                })
        },

        canEdit() {
            return this.record && this.record.status === 'pending'
        },

        editLeave(record) {
            this.leave = {...record}
            this.selected_leave = this.leave_types.find(a => a.id === record.type)
            this.handover_staff = this.staff.find(a => a.id === record.handover_staff)
            this.activeTab = 2
            this.record = ''
        },

        emptyForm() {
            this.leave = {
                id: '',
                type: '',
                start_date: '',
                days: 0,
                handover_staff: '',
                memo: ''
            }
            this.selected_leave = {}
            this.handover_staff = ''
            this.leave_end_date = ''
        },

        deleteLeave(record) {
            if (confirm('Are you sure you want to delete this leave request? This action cannot be undone.')) {
                this.setLoading()

                this.$http.delete(`user/leaves/${record.id}?delete=true`)
                    .then(() => {
                        this.success = 'Leave request deleted successfully!'
                        this.record = ''
                        this.loadLeaves();
                        setTimeout(() => (this.success = ''), 2000)
                    })
                    .catch(error => {
                        this.showError(error)
                    })
                    .finally(() => {
                        this.wait = false
                    })
            }
        },

        sendApplication() {
            this.clear()

            // Validation
            if (!this.leave.type) {
                this.error = 'Please select a leave type'
                return
            }
            if (!this.leave.start_date) {
                this.error = 'Please select a start date'
                return
            }
            if (!this.leave.days || this.leave.days < 1) {
                this.error = 'Please enter the number of leave days'
                return
            }
            if (!this.leave.handover_staff) {
                this.error = 'Please select a handover staff member'
                return
            }

            this.setLoading()
            this.success = "Submitting your application, please wait..."
            this.leave.start_date = this.formatDate(this.leave.start_date)

            this.$http.post(`user/leaves/${this.leave.id}`, this.leave)
                .then(res => {
                    this.success = "✅ Your leave request has been submitted successfully!"
                    this.$toast.success('Leave application submitted!')

                    setTimeout(() => {
                        window.location = '/leave'
                    }, 1500)
                })
                .catch(error => {
                    this.showError(error)
                })
                .finally(() => {
                    this.wait = false
                })
        },

        // Helper methods
        clear() {
            this.success = ''
            this.error = ''
        },

        setLoading() {
            this.wait = true
            this.clear()
        },

        showError(error) {
            if (error.response && error.response.data && error.response.data.message) {
                this.error = error.response.data.message
            } else {
                this.error = 'An error occurred. Please try again.'
            }
        },

        formatDate(date) {
            if (!date) return ''
            const d = new Date(date)
            const year = d.getFullYear()
            const month = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        },

        day(date) {
            // Assuming you have dayjs or moment.js
            return this.$dayjs ? this.$dayjs(date) : {
                format: (fmt) => new Date(date).toLocaleDateString()
            }
        }
    }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.nav-tabs {
    border-bottom: 2px solid #dee2e6;
}

.nav-tabs .nav-link {
    font-weight: 600;
    color: #6c757d;
    border: none;
    border-bottom: 3px solid transparent;
    padding: 1rem 1.5rem;
    transition: all 0.3s ease;
}

.nav-tabs .nav-link:hover {
    border-bottom-color: #007bff;
    color: #007bff;
}

.nav-tabs .nav-link.active {
    color: #007bff;
    border-bottom-color: #007bff;
    background-color: transparent;
}

.hover-shadow:hover {
    transform: translateY(-2px);
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.accordion a {
    text-decoration: none;
    color: #333;
    display: block;
    transition: all 0.3s ease;
}

.accordion a:hover {
    color: #007bff;
    background-color: #f8f9fa;
}

.badge-lg {
    font-size: 0.9rem;
    padding: 0.35rem 0.65rem;
}

.card {
    border-radius: 0.5rem;
    border: 1px solid #dee2e6;
}

.card-header {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
}

.form-control:focus,
.multiselect:focus-within {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.btn {
    transition: all 0.3s ease;
}

.btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.1);
}

@media (max-width: 768px) {
    .nav-tabs .nav-link {
        padding: 0.75rem 0.5rem;
        font-size: 0.875rem;
    }
}
</style>